import { create } from "zustand";
import {
  Message,
  ChatState,
  UserContext,
  ProjectSummary,
  ProjectContextItem,
  RecentProject,
} from "../types/chat";
import { supabase } from "../lib/supabase";
import { projectsService } from "../services/api/projects.service";
import { integrationsService } from "../services/api/integrations.service";
import { Project } from "../services/api/types";

interface ChatStore extends ChatState {
  sendMessage: (text: string) => Promise<void>;
  clearChat: () => void;
  chatState: "idle" | "loading" | "error";
  streamingMessageId: string | null;
}

// LocalStorage key for chat messages
const CHAT_STORAGE_KEY = "maat-chat-messages";

// Helper functions for localStorage
const loadMessagesFromStorage = (): Message[] => {
  try {
    const stored = localStorage.getItem(CHAT_STORAGE_KEY);
    if (stored) {
      const messages = JSON.parse(stored) as Message[];
      // Validate that it's an array of messages
      if (
        Array.isArray(messages) &&
        messages.every(
          (msg) => msg.id && msg.text && msg.sender && msg.timestamp,
        )
      ) {
        return messages;
      }
    }
  } catch (error) {
    console.warn("Failed to load chat messages from storage:", error);
  }
  return [];
};

const saveMessagesToStorage = (messages: Message[]): void => {
  try {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
  } catch (error) {
    console.warn("Failed to save chat messages to storage:", error);
  }
};

const clearMessagesFromStorage = (): void => {
  try {
    localStorage.removeItem(CHAT_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear chat messages from storage:", error);
  }
};

export const useChatStore = create<ChatStore>((set, get) => {
  // Context cache
  let contextCache: UserContext | null = null;
  let contextCacheTime = 0;
  const CACHE_TTL = 60000; // 60 seconds

  // Load messages from localStorage on initialization
  const initialMessages = loadMessagesFromStorage();

  /**
   * Build user context from projects and integrations
   */
  const buildUserContext = async (): Promise<UserContext> => {
    const now = Date.now();

    // Return cached context if still valid
    if (contextCache && now - contextCacheTime < CACHE_TTL) {
      return contextCache;
    }

    try {
      // Fetch projects and integrations in parallel
      const [projects, integrations] = await Promise.all([
        projectsService.getAll().catch(() => [] as Project[]),
        integrationsService.getAll().catch(() => []),
      ]);

      // Calculate project summary
      const projectSummary: ProjectSummary = {
        total: projects.length,
        active: projects.filter((p) => p.status === "active").length,
        byStatus: {
          active: projects.filter((p) => p.status === "active").length,
          planned: projects.filter((p) => p.status === "planned").length,
          "on-hold": projects.filter((p) => p.status === "on-hold").length,
          completed: projects.filter((p) => p.status === "completed").length,
        },
        byPriority: {
          high: projects.filter((p) => p.priority === "high").length,
          medium: projects.filter((p) => p.priority === "medium").length,
          low: projects.filter((p) => p.priority === "low").length,
        },
      };

      // Build active projects list (max 10, with essential fields)
      const activeProjects: ProjectContextItem[] = projects
        .filter((p) => p.status === "active")
        .slice(0, 10)
        .map((p) => {
          let daysRemaining: number | undefined;
          if (p.target_end_date) {
            const endDate = new Date(p.target_end_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            const diffTime = endDate.getTime() - today.getTime();
            daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          }

          return {
            name: p.name,
            status: p.status || "active",
            priority: p.priority || "medium",
            department: p.department,
            type: p.project_type,
            daysRemaining:
              daysRemaining && daysRemaining > 0 ? daysRemaining : undefined,
          };
        });

      // Build recent projects list (max 5, minimal fields)
      const recentProjects: RecentProject[] = projects.slice(0, 5).map((p) => ({
        name: p.name,
        status: p.status || "unknown",
      }));

      // Get connected integrations
      const connectedIntegrations = integrations
        .filter((i) => i.connected)
        .map((i) => i.type);

      // Check if Jira is connected
      const jiraInfo = connectedIntegrations.includes("jira");

      // Build context
      const context: UserContext = {
        projectSummary,
        activeProjects,
        recentProjects,
        currentSprint: "Not set", // Can be enhanced later
        connectedIntegrations,
        activeWorkItems: 0, // Can be enhanced later
        recentWorkPeriods: 0, // Can be enhanced later
        teamPatterns: {}, // Can be enhanced later
        teamMembers: {}, // Can be enhanced later
        recentConversations: get().messages.length,
        jiraInfo,
      };

      // Cache the context
      contextCache = context;
      contextCacheTime = now;

      return context;
    } catch (error) {
      console.error("Failed to build user context:", error);
      // Return empty context on error (graceful degradation)
      const fallbackContext: UserContext = {
        projectSummary: {
          total: 0,
          active: 0,
          byStatus: {},
          byPriority: {},
        },
        activeProjects: [],
        recentProjects: [],
        currentSprint: "Not set",
        connectedIntegrations: [],
        activeWorkItems: 0,
        recentWorkPeriods: 0,
        teamPatterns: {},
        teamMembers: {},
        recentConversations: get().messages.length,
        jiraInfo: false,
      };
      return fallbackContext;
    }
  };

  return {
    messages: initialMessages,
    isLoading: false,
    error: null,
    chatState: "idle",
    streamingMessageId: null,

    sendMessage: async (text: string) => {
      const userMessage: Message = {
        id: Date.now().toString(),
        text,
        sender: "user",
        timestamp: new Date().toISOString(),
      };

      const aiMessageId = (Date.now() + 1).toString();

      const updatedMessages = [...get().messages, userMessage];
      set({
        messages: updatedMessages,
        isLoading: true,
        chatState: "loading",
        streamingMessageId: null,
      });
      saveMessagesToStorage(updatedMessages);

      try {
        // Check if Supabase client is available
        if (!supabase) {
          throw new Error("Supabase client not available");
        }

        // Build user context with real project data
        const context = await buildUserContext();

        // Convert existing messages to conversation history format
        // Exclude the current user message since it's sent separately
        // Limit to last 20 messages to avoid token limits (keep recent context)
        const allMessages = get().messages.filter(
          (msg) => msg.id !== userMessage.id,
        );
        const recentMessages = allMessages.slice(-20); // Keep last 20 messages for context
        const conversationHistory = recentMessages.map((msg) => ({
          role: msg.sender === "user" ? "user" : "assistant",
          content: msg.text,
        }));

        console.log(
          `📜 Sending conversation history: ${conversationHistory.length} messages`,
        );

        // Get session token for authentication
        const {
          data: { session },
        } = (await supabase?.auth.getSession()) || { data: { session: null } };
        const authToken =
          session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

        // Call Supabase Edge Function directly with fetch for streaming
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/maat-chat`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              message: text,
              context,
              conversationHistory,
            }),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Edge function error:", response.status, errorData);

          // Provide a helpful fallback response
          const fallbackResponse: Message = {
            id: aiMessageId,
            text: `I received your message: "${text}". I'm Maat AI, your project management assistant. I'm currently experiencing some technical difficulties, but I can still help you with basic project management guidance. What would you like to know about managing your projects?`,
            sender: "ai",
            timestamp: new Date().toISOString(),
          };

          const updatedMessages = [...get().messages, fallbackResponse];
          set({
            messages: updatedMessages,
            isLoading: false,
            chatState: "idle",
            streamingMessageId: null,
          });
          saveMessagesToStorage(updatedMessages);
          return;
        }

        // Handle streaming response
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No response body available for streaming");
        }

        const decoder = new TextDecoder();
        let buffer = "";

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                const data = line.slice(6);
                if (data === "[DONE]") {
                  set({
                    isLoading: false,
                    chatState: "idle",
                    streamingMessageId: null,
                  });
                  // Save final state when streaming completes
                  saveMessagesToStorage(get().messages);
                  return;
                }

                try {
                  const parsed = JSON.parse(data);
                  if (parsed.type === "content" && parsed.content) {
                    // Set streaming message ID and create AI message when we start receiving content
                    if (!get().streamingMessageId) {
                      const aiMessage: Message = {
                        id: aiMessageId,
                        text: parsed.content,
                        sender: "ai",
                        timestamp: new Date().toISOString(),
                      };

                      const updatedMessages = [...get().messages, aiMessage];
                      set({
                        streamingMessageId: aiMessageId,
                        messages: updatedMessages,
                      });
                      saveMessagesToStorage(updatedMessages);
                    } else {
                      // Update the AI message with new content
                      const updatedMessages = get().messages.map((msg) =>
                        msg.id === aiMessageId
                          ? { ...msg, text: msg.text + parsed.content }
                          : msg,
                      );
                      set({
                        messages: updatedMessages,
                      });
                      saveMessagesToStorage(updatedMessages);
                    }
                  } else if (parsed.type === "error") {
                    throw new Error(parsed.error || "Streaming error");
                  }
                } catch (e) {
                  console.warn("Failed to parse streaming data:", data);
                }
              }
            }
          }
        } finally {
          set({
            isLoading: false,
            chatState: "idle",
            streamingMessageId: null,
          });
          // Ensure final state is saved even if streaming ends unexpectedly
          saveMessagesToStorage(get().messages);
        }
      } catch (error) {
        console.error("Chat error:", error);

        // Provide a more helpful error message based on the error type
        let errorMessage =
          "I'm sorry, I'm having trouble connecting right now. Please check your connection and try again.";

        if (error instanceof Error) {
          if (error.message.includes("Failed to send a request")) {
            errorMessage =
              "I'm experiencing some technical difficulties with the AI service. I can still help you with basic project management guidance. What would you like to know about managing your projects?";
          } else if (error.message.includes("timeout")) {
            errorMessage =
              "The request is taking longer than expected. Please try again in a moment.";
          }
        }

        const errorResponse: Message = {
          id: aiMessageId,
          text: errorMessage,
          sender: "ai",
          timestamp: new Date().toISOString(),
        };

        const updatedMessages = [...get().messages, errorResponse];
        set({
          messages: updatedMessages,
          isLoading: false,
          chatState: "error",
          streamingMessageId: null,
          error: error instanceof Error ? error.message : "Unknown error",
        });
        saveMessagesToStorage(updatedMessages);
      }
    },

    clearChat: () => {
      set({ messages: [], error: null, chatState: "idle" });
      clearMessagesFromStorage();
    },
  };
});
